import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import FableticsModal from '~/shared/components/FableticsModal';
import { usePDPOptionLabels } from '~/shared/utils/useLabels';
import {
  AttributeFieldTypes,
  Modal,
  mobile,
  OptionLabelProvider,
  ProductContext,
  ProductImage,
  ProductName as BaseProductName,
  Separator,
  useProductDetailContext,
  useProductDetailSelectionState,
  useProductAttributeFieldType,
  useCollapsibleState,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, FormattedDate } from '~/techstyle-shared/react-intl';

import { ProductDetailLabelWithContext } from '../ProductDetailLabels';
import ProductDetailPrice from '../ProductDetailPrice';

const sideImageBreakpoints = [{ minWidth: 0, pictureWidth: 208 }];
const topImageBreakpoints = [{ minWidth: 0, pictureWidth: 69 }];

const contentWrapperStyles = css`
  padding: 32px;
  display: flex;
  ${mobile`
    padding: 16px;
  `};
`;

const priceContentStyles = css`
  margin-bottom: 0;
`;

const SideProductImage = styled.div`
  margin-right: 24px;
  ${({ $sideProductImageStyles }) => $sideProductImageStyles};
`;

const TopProductImage = styled.div`
  margin-right: 12px;
  ${({ $topProductImageStyles }) => $topProductImageStyles};
`;

const ProductWrapper = styled.div`
  width: 482px;
  ${({ $isCollapsibleContent }) =>
    $isCollapsibleContent &&
    css`
      width: auto;
    `}
`;

const ProductContent = styled.div`
  display: flex;
`;

const ProductNameWrapper = styled.div`
  margin-bottom: 4px;
  font-weight: 600;
`;

const OutfitProductName = styled.div``;

const ProductName = styled(BaseProductName)`
  font-size: 16px;
  line-height: 1.25;
`;

const SelectedInfoWrapper = styled.div`
  color: ${({ theme }) => theme.colors.productDetailCtaModalInfo};
  line-height: 1.5;
`;

const selectedInfoTextStyle = css`
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const SelectedShippingInfoText = styled.span`
  color: ${({ theme }) => theme.colors.pdpShippingInfo};
`;

const ProductInfoDetails = styled.div``;

const separatorStyle = css`
  margin: 16px 0 24px;
  color: ${({ theme }) => theme.colors.productDetailSeparator};
  ${mobile` 
    margin-bottom: 16px;
  `}
`;

const OutfitOptionLabel = () => {
  const color = useProductAttributeFieldType(AttributeFieldTypes.COLOR);
  const size = useProductAttributeFieldType(AttributeFieldTypes.SIZE);
  return (
    <>
      <ProductDetailLabelWithContext
        displayFilterField={false}
        infoTextStyle={selectedInfoTextStyle}
        field={color}
      />
      {', '}
      <ProductDetailLabelWithContext
        displayFilterField={false}
        infoTextStyle={selectedInfoTextStyle}
        field={size}
      />
    </>
  );
};
export default function ProductDetailCTAModal({
  children,
  closeButtonStyles,
  isPlusSizeSelected,
  dialogProps,
  displayShippingDate = false,
  showLabels = false,
  showPrices = false,
  sideProductImageStyles,
  topProductImageStyles,
  ...rest
}) {
  const productDetailFromCtx = useProductDetailContext();
  const isOutfit = productDetailFromCtx.isBundle;
  const outfitLength =
    productDetailFromCtx.product.bundleComponentProducts?.length;
  const { selectedSKU } = useProductDetailSelectionState();
  const color = useProductAttributeFieldType(AttributeFieldTypes.COLOR);
  const size = useProductAttributeFieldType(AttributeFieldTypes.SIZE);
  const isCollapsibleContent = useCollapsibleState();

  return isCollapsibleContent ? (
    <>{children}</>
  ) : (
    <FableticsModal
      as={Modal}
      closeButtonStyles={closeButtonStyles}
      contentWrapperStyles={contentWrapperStyles}
      dialogProps={dialogProps}
      dialogStyle="padding: 0"
      {...rest}
    >
      <SideProductImage $sideProductImageStyles={sideProductImageStyles}>
        <ProductImage
          breakpoints={sideImageBreakpoints}
          disableHover
          type={isPlusSizeSelected ? 'plus' : 'default'}
        />
      </SideProductImage>
      <ProductWrapper $isCollapsibleContent={!!isCollapsibleContent}>
        <ProductContent>
          <TopProductImage $topProductImageStyles={topProductImageStyles}>
            <ProductImage
              breakpoints={topImageBreakpoints}
              disableHover
              type={isPlusSizeSelected ? 'plus' : 'default'}
            />
          </TopProductImage>
          <ProductInfoDetails>
            <ProductNameWrapper>
              {isOutfit ? (
                <OutfitProductName data-autotag="pdp-grid-product-name">
                  <FormattedMessage
                    id="site_pdp.outfit_pieces"
                    defaultMessage="{count}-Piece"
                    values={{ count: outfitLength }}
                  />{' '}
                  <ProductName />
                </OutfitProductName>
              ) : (
                <ProductName data-autotag="pdp-grid-product-name" />
              )}
            </ProductNameWrapper>
            {displayShippingDate && (
              <SelectedInfoWrapper>
                <FormattedMessage
                  defaultMessage="Estimated Ship Date:"
                  id="site_pdp.estimated_ship_date"
                />
                <SelectedShippingInfoText>
                  {selectedSKU?.dateAvailablePreorder && (
                    <FormattedDate
                      value={selectedSKU.dateAvailablePreorder}
                      month="2-digit"
                      day="2-digit"
                      year="2-digit"
                    />
                  )}
                </SelectedShippingInfoText>
              </SelectedInfoWrapper>
            )}
            {showPrices ? (
              <ProductDetailPrice
                priceContentStyles={priceContentStyles}
                showEMPPrice={false}
              />
            ) : null}
            {showLabels ? (
              <OptionLabelProvider useLabels={usePDPOptionLabels}>
                {isOutfit ? (
                  productDetailFromCtx.bundleProducts.map((product, index) => (
                    <ProductContext
                      key={product.masterProductId}
                      product={product}
                      productIndex={index}
                    >
                      <SelectedInfoWrapper>
                        <ProductName />
                        {': '}
                        <OutfitOptionLabel />
                      </SelectedInfoWrapper>
                    </ProductContext>
                  ))
                ) : (
                  <>
                    <ProductDetailLabelWithContext
                      infoTextStyle={selectedInfoTextStyle}
                      displayFilterField={false}
                      field={color}
                    />
                    {', '}
                    <ProductDetailLabelWithContext
                      infoTextStyle={selectedInfoTextStyle}
                      displayFilterField={false}
                      field={size}
                    />
                  </>
                )}
              </OptionLabelProvider>
            ) : null}
          </ProductInfoDetails>
        </ProductContent>
        <Separator thickness={1} separatorStyle={separatorStyle} />
        {children}
      </ProductWrapper>
    </FableticsModal>
  );
}

ProductDetailCTAModal.propTypes = {
  children: PropTypes.any,
  closeButtonStyles: PropTypes.object,
  dialogProps: PropTypes.object,
  displayShippingDate: PropTypes.bool,
  isPlusSizeSelected: PropTypes.bool,
  showLabels: PropTypes.bool,
  showPrices: PropTypes.bool,
  sideProductImageStyles: PropTypes.any,
  topProductImageStyles: PropTypes.any,
};
